import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useGetSmetaInfoQuery } from '@shared/api';
import { Card, CardTitle, CardSubtitle } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const FnTitle = styled.span`
  flex: 1;
`;

const Description = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const Note = styled.div`
  padding: ${({ theme }) => theme.spacing(4)} 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ToolsBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const FullHeightCard = styled(Card)`
  height: 100%;
`;

/** Маршрут раздела для функции, если она уже реализована. */
const FN_ROUTE: Record<number, string> = {
  1: '/agents/smeta/volume-checks',
  // Функция 2 (проверка расценок) доделана: Stage A–F2 + D-PR2 в проде —
  // нормативная база, движок сверки ФЕР/ГЭСН + НР/СП, семантический матч,
  // LLM-рекомендации, справка по БИМ-индексам. Вход — список проверок.
  2: '/agents/smeta/rate-checks',
  // Функция 3 (проверка КС-2/КС-3, ADR-0004): сверка актов выполненных работ
  // с договорной сметой — цены/объёмы/арифметика/НР-СП + reconciliation КС-3.
  3: '/agents/smeta/ks-checks',
  // Функция 4 (списание материалов / М-29, ADR-0005): загрузка КС-2 + накладных,
  // нормативный расход по ГЭСН/ФЕР × факт списания, перерасход/экономия + отчёт М-29.
  4: '/agents/smeta/write-off-checks',
  // Функция 5 (сметный расчёт по тендеру, ADR-0006): загрузка перечня работ →
  // построение ЛСР по нормативу ФЕР/ГЭСН + поиск ошибок + сравнение с аналогами.
  5: '/agents/smeta/tender-estimates',
  // Функция 6 (новые расценки / ЗВИ, ADR-0008): загрузка формы ЗВИ + РД (BOM/BOQ)
  // → сверка объёмов и ставок ЕР с договором + арифметика/косвенные + новые ЕР.
  6: '/agents/smeta/zvi-checks',
  // Функция 7 (накопительные таблицы, ADR-0007): загрузка пакета накладных +
  // актов КС-2 → реестры по месяцам/участкам + сводная аналитика в Excel.
  7: '/agents/smeta/registers',
  // Функция 8 (сверка Генподряд/Субподряд, ADR-0009): загрузка пакетов двух
  // сторон → матч позиций ГП↔СП, маржа генподряда, покрытие, расхождения + XLSX.
  8: '/agents/smeta/gs-checks',
  // Функция 9 (чат-ассистент, ADR-0010): диалог с ИИ-сметчиком поверх
  // результатов всех функций + методические знания. Read-only, объясняет.
  9: '/agents/smeta/chat',
};

export function SmetaPage() {
  const { data, isLoading, isError } = useGetSmetaInfoQuery();
  // Доступность разделов берём из ответа сервера: `/agents/smeta/info` отдаёт
  // состояние фичефлагов. Раньше её определяли пробным запросом по
  // /ks-packages («пришёл ли 404»), но после разделения флагов проба врёт: при
  // включённом импорте ЛСР и выключенном пакете акта она по-прежнему вернёт
  // 404, и работающий раздел смет остался бы спрятанным.
  // `?? false` — на случай отката backend'а к версии без поля: раздел просто не
  // покажется, вместо падения страницы (ErrorBoundary в приложении нет).
  const ksPackagesAvailable = data?.flags?.ks_package ?? false;
  const lsrImportAvailable = data?.flags?.lsr_import ?? false;

  return (
    <div>
      <SectionLabel>Функции агента</SectionLabel>

      {isLoading && <Note>Загрузка описания агента…</Note>}
      {isError && <Note>Не удалось загрузить описание агента. Обновите страницу.</Note>}

      {data && (
        <Grid>
          {data.functions.map((f, i) => {
            const route = FN_ROUTE[f.id];
            const available = Boolean(route);
            const body = (
              <FullHeightCard
                $interactive={available}
                $accent={available}
                $delay={60 + i * 60}
              >
                <CardTitle>
                  <FnTitle>{f.title}</FnTitle>
                  {available ? (
                    <Badge $tone="success">доступно</Badge>
                  ) : (
                    <Badge $tone="neutral">в разработке</Badge>
                  )}
                </CardTitle>
                <CardSubtitle>Функция {f.id}</CardSubtitle>
                <Description>{f.description}</Description>
              </FullHeightCard>
            );
            return route ? (
              <CardLink key={f.id} to={route}>
                {body}
              </CardLink>
            ) : (
              <div key={f.id}>{body}</div>
            );
          })}
        </Grid>
      )}

      {/* Админ-инструмент нормативной базы Функции 2 — управление
          поквартальными индексами ФГИС ЦС (загрузка через CLI, HITL-активация).
          Сам вход в проверку расценок — карточка «Функция 2» выше. */}
      <ToolsBlock>
        <SectionLabel>Вспомогательные инструменты и реестры закрытия</SectionLabel>
        <Grid>
          {/* Пакет акта — продолжение Функции 3: та ПРОВЕРЯЕТ загруженный КС-2,
              а этот раздел СОБИРАЕТ комплект закрытия из принятых объёмов.
              Показываем только при включённом фичефлаге. */}
          {ksPackagesAvailable && (
            <CardLink to="/agents/smeta/ks-packages">
              <FullHeightCard $interactive $accent>
                <CardTitle>
                  <FnTitle>Пакет акта (КС-2 / КС-3)</FnTitle>
                  <Badge $tone="success">доступно</Badge>
                </CardTitle>
                <CardSubtitle>Сборка комплекта закрытия</CardSubtitle>
                <Description>
                  Договорная смета + период → акты КС-2 со строками из принятых объёмов, матч на
                  позиции сметы, итоги по статьям и находки с HITL-разметкой. Сборка идёт в
                  очереди, неоднозначный матч разрешается вручную.
                </Description>
              </FullHeightCard>
            </CardLink>
          )}
          <CardLink to="/agents/smeta/accumulations">
            <FullHeightCard $interactive $accent>
              <CardTitle>
                <FnTitle>Накопительный учёт ПА</FnTitle>
                <Badge $tone="success">доступно</Badge>
              </CardTitle>
              <CardSubtitle>Функция 7B · лента первичных актов (ADR-0011)</CardSubtitle>
              <Description>
                Накопительная книга первичных актов по договору и объекту: импорт существующей
                книги, ежемесячное дозаполнение пачками ПА, привязка к договору и проекту ПТО,
                сквозная передача объёмов в Прогресс и Пакет акта КС-2/КС-3.
              </Description>
            </FullHeightCard>
          </CardLink>
          {/* Нормативная база ФСНБ-2022: справочник, на котором считают все
              проверки расценок, и его редакции (загрузка дополнения с ФГИС ЦС,
              проверка изменений, ввод в действие). */}
          <CardLink to="/agents/smeta/norm-base">
            <FullHeightCard $interactive $accent>
              <CardTitle>
                <FnTitle>Нормативная база ФСНБ-2022</FnTitle>
                <Badge $tone="success">доступно</Badge>
              </CardTitle>
              <CardSubtitle>Нормы ГЭСН, цены ФСБЦ, редакции</CardSubtitle>
              <Description>
                Поиск нормы по шифру или словам, карточка с составом работ, ресурсами и ценами.
                Новое дополнение с ФГИС ЦС загружается архивом: видно, какие нормы появились и
                изменились, в действие его вводят вручную.
              </Description>
            </FullHeightCard>
          </CardLink>
          <CardLink to="/agents/smeta/fgiscs-periods">
            <FullHeightCard $interactive $accent>
              <CardTitle>
                <FnTitle>Поквартальные индексы ФГИС ЦС</FnTitle>
                <Badge $tone="success">доступно</Badge>
              </CardTitle>
              <CardSubtitle>HITL approve (ADR-0003)</CardSubtitle>
              <Description>
                Просмотр загруженных через CLI периодов индексов и ставок ОЗП, сравнение с активным
                кварталом и активация одной кнопкой. Часть Функции 2 — нормативная база для проверки
                единичных расценок.
              </Description>
            </FullHeightCard>
          </CardLink>
          {/* Локальные сметы — свой фичефлаг `OSNOVA_LSR_IMPORT_ENABLED`: их
              роуты живут в отдельной группе и открываются раньше остального
              пакета акта. Гейт обязателен: без него плитка вела бы на страницу,
              где любой запрос отдаёт 404. */}
          {lsrImportAvailable && (
            <CardLink to="/agents/smeta/contract-lsr">
              <FullHeightCard $interactive $accent>
                <CardTitle>
                  <FnTitle>Локальные сметы договора</FnTitle>
                  <Badge $tone="success">доступно</Badge>
                </CardTitle>
                <CardSubtitle>Эталон с расшифровкой цены</CardSubtitle>
                <Description>
                  Книга смет Заказчика загружается целиком: каждый лист выгрузки Гранд-Сметы —
                  отдельная смета. Позиции приходят с зарплатой рабочих, эксплуатацией машин и
                  трудозатратами — источник граф 7-16 КС-2, М-29 и фонда оплаты труда.
                </Description>
              </FullHeightCard>
            </CardLink>
          )}
          <CardLink to="/agents/smeta/material-norms">
            <FullHeightCard $interactive $accent>
              <CardTitle>
                <FnTitle>Нормы расхода материалов</FnTitle>
                <Badge $tone="success">доступно</Badge>
              </CardTitle>
              <CardSubtitle>Коэффициенты потерь по классам и договорам</CardSubtitle>
              <Description>
                Сколько материала списывается сверх проектного объёма: резка трубы, бой кирпича,
                нахлёст рулона. Правило договора сильнее общего класса, а кнопка проверки
                показывает, какая норма подставится конкретному наименованию из акта.
              </Description>
            </FullHeightCard>
          </CardLink>
          <CardLink to="/agents/smeta/vat-rates">
            <FullHeightCard $interactive $accent>
              <CardTitle>
                <FnTitle>Ставки НДС</FnTitle>
                <Badge $tone="success">доступно</Badge>
              </CardTitle>
              <CardSubtitle>Справочник по периодам</CardSubtitle>
              <Description>
                Ставка берётся по дате документа: акт за прошлый год считается по ставке своего
                периода, а не по действующей сегодня. Новую ставку можно завести заранее — она
                включится в свою дату. Тот же справочник видит экономист.
              </Description>
            </FullHeightCard>
          </CardLink>
        </Grid>
      </ToolsBlock>
    </div>
  );
}
